/**
 * @module UploadReceiptModalPickForm
 * @purpose The pick-state form body for UploadReceiptModal — target picker + file pickers + text fields
 * @depends upload-receipt-modal.parts (TargetPicker, FilePicker, TextField), useUploadReceiptModal state
 * @dependedOnBy UploadReceiptModal
 *
 * @rules
 * - State: receives the modal state via props; never owns its own state
 * - Errors: pick-state inline error rendered when state.error is set and step === 'pick'
 */

import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { TargetPicker, FilePicker, TextField } from './upload-receipt-modal.parts'
import type { UploadReceiptModalState } from './use-upload-receipt-modal'

interface PickStateFormProps {
  state: UploadReceiptModalState
  t: TFunction<'cases'>
}

/**
 * Form body shown in the 'pick' step (and reused while 'uploading' / 'error' until success).
 *
 * @behaviour Renders target picker, zip + log file pickers, OOB hash + notes text fields.
 *            Inline pick-state error appears under the zip picker when a client-side validation fails.
 */
export function PickStateForm({ state, t }: PickStateFormProps): ReactElement {
  return (
    <>
      <TargetPicker
        targets={state.targets}
        target={state.target}
        loading={state.targetsLoading}
        showAll={state.showAll}
        onSelect={state.setTarget}
        onToggleShowAll={state.toggleShowAll}
        t={t}
      />
      <FilePicker
        id="upload-zip"
        label={t('detail.collections.uploadReceipt.zipLabel')}
        accept=".zip"
        file={state.zipFile}
        onChange={state.setZipFile}
      />
      {state.error && state.step === 'pick' && (
        <p className="text-sm text-red-600 dark:text-red-400" role="alert">
          {state.error}
        </p>
      )}
      <FilePicker
        id="upload-log"
        label={t('detail.collections.uploadReceipt.logLabel')}
        accept=".log"
        file={state.logFile}
        onChange={state.setLogFile}
      />
      <TextField
        id="upload-oob-hash"
        label={t('detail.collections.uploadReceipt.oobHashLabel')}
        value={state.oobHash}
        onChange={state.setOobHash}
      />
      <TextField
        id="upload-notes"
        label={t('detail.collections.uploadReceipt.notesLabel')}
        value={state.notes}
        onChange={state.setNotes}
      />
    </>
  )
}
